/**
 * Custom SWR Cache Provider with LRU eviction
 *
 * Bounds memory by capping the number of cached entries (MAX_CACHE_SIZE) and evicting the least
 * recently used ones once the cap is exceeded. There is deliberately no time-based sweep: it evicted
 * entries that a mounted page was still showing, and with revalidateIfStale off nothing fetched them
 * again, so an idle tab came back empty.
 */

// An admin session routinely accumulates hundreds of unique SWR keys across a few
// page visits (workers, invoices, payrolls, time-off, expenses, etc.). At 100,
// LRU eviction was forcing re-fetches on return navigation for large tenants.
const MAX_CACHE_SIZE = 500;

class LRUCache<K, V> extends Map<K, V> {
  private accessTimes = new Map<K, number>();
  private maxSize: number;

  constructor(maxSize: number) {
    super();
    this.maxSize = maxSize;
  }

  get(key: K): V | undefined {
    const value = super.get(key);
    if (value !== undefined) {
      this.accessTimes.set(key, Date.now());
    }
    return value;
  }

  set(key: K, value: V): this {
    this.accessTimes.set(key, Date.now());
    super.set(key, value);
    this.evictIfNeeded();
    return this;
  }

  delete(key: K): boolean {
    this.accessTimes.delete(key);
    return super.delete(key);
  }

  private evictIfNeeded(): void {
    if (this.size <= this.maxSize) return;

    // Find and remove the least recently used entries
    const entries = Array.from(this.accessTimes.entries());
    entries.sort((a, b) => a[1] - b[1]);

    const toRemove = this.size - this.maxSize;
    for (let i = 0; i < toRemove; i++) {
      this.delete(entries[i][0]);
    }
  }

  clear(): void {
    this.accessTimes.clear();
    super.clear();
  }
}

// Hold a reference to the underlying cache so we can clear it on user switch
let cacheInstance: LRUCache<string, unknown> | null = null;

export function createLRUCacheProvider() {
  const cache = new LRUCache<string, unknown>(MAX_CACHE_SIZE);
  cacheInstance = cache;

  return () => cache as Map<string, unknown>;
}

/**
 * Clear all entries from the SWR cache.
 * Call this during logout / session switch to prevent stale user data
 * from leaking into the next session (e.g. super admin magic-link impersonation).
 */
export const clearSWRCache = (): void => {
  cacheInstance?.clear();
};

// Export a singleton cache provider for use with SWRConfig
export const swrCacheProvider = createLRUCacheProvider();
